import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { HeatmapQuestion } from '../../fetchSurvey/types/heatmap';
import { LOGIC_FIELDS } from '../constants/logicFields';
import type { LogicContext, ResolvedFieldValue } from '../types';
import { buildMatrixCompositeKeys } from './buildMatrixCompositeKeys';
import { getMatchedRegionLabels, normalizeHeatmapClicks } from './heatmapRegionUtils';
import { isMatrixRowAnswers } from './matrixQuestionUtils';
import {
  normalizeFileUpload,
  normalizeSelectedOptions,
  normalizeText,
} from './normalizeFieldAnswers';
import {
  normalizeRankOrderAnswers,
  getOptionRank,
} from './rankOrderUtils';

export function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function resolveFieldValue(
  field: string | undefined,
  questionId: string | undefined,
  context: LogicContext,
  subConditionOptionId?: string
): ResolvedFieldValue {
  if (!field || !questionId) return undefined;

  const answer = context.answers[questionId];
  const question = context.questionsById.get(questionId);

  switch (field) {
    case LOGIC_FIELDS.TEN_POINT_SCALE:
      return toNumber(answer);

    case LOGIC_FIELDS.SLIDER:
      // SLIDER skip-logic field applies to SLIDER_MATRIX only (row map answers).
      if (question?.type === QUESTION_TYPE.SLIDER_MATRIX && isMatrixRowAnswers(answer)) {
        return answer;
      }
      return undefined;

    case LOGIC_FIELDS.SELECTED_OPTIONS:
      return normalizeSelectedOptions(answer);

    case LOGIC_FIELDS.NUM_OF_SELECTED_OPTIONS:
      return normalizeSelectedOptions(answer).length;

    case LOGIC_FIELDS.TEXT:
    case LOGIC_FIELDS.CASE_SENSITIVE_TEXT:
      return normalizeText(answer);

    case LOGIC_FIELDS.MATRIX_OPTIONS:
      if (isMatrixRowAnswers(answer)) {
        return buildMatrixCompositeKeys(questionId, answer, context);
      }
      return new Set<string>();

    case LOGIC_FIELDS.FILE_UPLOAD:
      return normalizeFileUpload(answer);

    case LOGIC_FIELDS.HEATMAP:
      return normalizeHeatmapClicks(answer);

    case LOGIC_FIELDS.HEATMAP_REGION: {
      const clicks = normalizeHeatmapClicks(answer);
      if (question?.type !== QUESTION_TYPE.HEATMAP) {
        return new Set<string>();
      }
      const heatmapQuestion = question as HeatmapQuestion;
      return new Set(getMatchedRegionLabels(clicks, heatmapQuestion.regions));
    }

    case LOGIC_FIELDS.RANK_ORDER: {
      if (!subConditionOptionId) {
        return undefined;
      }

      const rankAnswers = normalizeRankOrderAnswers(answer);
      return getOptionRank(rankAnswers, subConditionOptionId);
    }

    default:
      return answer;
  }
}

export function isScalarMissing(value: ResolvedFieldValue): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (value instanceof Set && value.size === 0) return true;
  return false;
}
